import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { ArrowLeft, Save, Plus, Trash2, LogOut } from "lucide-react";
import { useAdminAuth } from "@/hooks/useAdminAuth";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminBookContentPage = () => {
  const navigate = useNavigate();
  const { requireAuth, logout, getAuthHeaders } = useAdminAuth();
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  
  const [bookData, setBookData] = useState({
    title: "Pasteur Joseph Kalala - Le Mystère de la Croix",
    author: "Pasteur Joseph Kalala",
    introduction: "",
    chapters: [
      { chapter_number: 1, chapter_title: "Chapitre 1", content: "" },
      { chapter_number: 2, chapter_title: "Chapitre 2", content: "" },
      { chapter_number: 3, chapter_title: "Chapitre 3", content: "" }
    ],
    conclusion: ""
  });

  useEffect(() => {
    if (!requireAuth()) return;

    const fetchBook = async () => {
      try {
        // Note: On ne peut pas appeler /book/content car il nécessite un paiement
        // On va créer un endpoint spécial admin ou charger depuis la DB
        setFetching(false);
      } catch (error) {
        console.error("Erreur", error);
        if (error.response?.status === 403) {
          logout();
        }
        setFetching(false);
      }
    };

    fetchBook();
  }, [requireAuth, logout]);

  const handleChapterChange = (index, field, value) => {
    const newChapters = [...bookData.chapters];
    newChapters[index][field] = value;
    setBookData({ ...bookData, chapters: newChapters });
  };

  const addChapter = () => {
    const newChapterNum = bookData.chapters.length + 1;
    setBookData({
      ...bookData,
      chapters: [
        ...bookData.chapters,
        { chapter_number: newChapterNum, chapter_title: `Chapitre ${newChapterNum}`, content: "" }
      ]
    });
  };

  const removeChapter = (index) => {
    const newChapters = bookData.chapters.filter((_, i) => i !== index);
    // Réorganiser les numéros
    newChapters.forEach((ch, i) => {
      ch.chapter_number = i + 1;
      if (ch.chapter_title === `Chapitre ${index + 1}`) {
        ch.chapter_title = `Chapitre ${i + 1}`;
      }
    });
    setBookData({ ...bookData, chapters: newChapters });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.put(`${API}/admin/book/content`, bookData, {
        headers: getAuthHeaders()
      });
      toast.success("✅ Contenu du livre sauvegardé ! Vos lecteurs peuvent maintenant lire le livre.");
    } catch (error) {
      const message = error?.response?.data?.detail || "Erreur lors de la sauvegarde";
      toast.error(message);
      if (error.response?.status === 403) {
        logout();
      }
    } finally {
      setLoading(false);
    }
  };

  if (fetching) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-5xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              onClick={() => navigate("/admin/config")}
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Retour
            </Button>
            <h1 className="text-3xl font-bold text-gray-900">Contenu du Livre</h1>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => navigate("/admin/editor")}
            >
              Éditeur Site
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate("/admin/orders")}
            >
              Commandes
            </Button>
            <Button
              variant="destructive"
              onClick={logout}
              size="sm"
            >
              <LogOut className="mr-2 h-4 w-4" />
              Déconnexion
            </Button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Info Livre */}
          <Card>
            <CardHeader>
              <CardTitle>Informations du Livre</CardTitle>
              <CardDescription>
                Titre et auteur du livre
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Titre du livre</Label>
                <Input
                  value={bookData.title}
                  onChange={(e) => setBookData({ ...bookData, title: e.target.value })}
                  placeholder="Pasteur Joseph Kalala - Le Mystère de la Croix"
                />
              </div>
              <div>
                <Label>Auteur</Label>
                <Input
                  value={bookData.author}
                  onChange={(e) => setBookData({ ...bookData, author: e.target.value })}
                  placeholder="Pasteur Joseph Kalala"
                />
              </div>
            </CardContent>
          </Card>

          {/* Introduction */}
          <Card>
            <CardHeader>
              <CardTitle>Introduction</CardTitle>
            </CardHeader>
            <CardContent>
              <Textarea
                value={bookData.introduction}
                onChange={(e) => setBookData({ ...bookData, introduction: e.target.value })}
                placeholder="Collez ici le texte de l'introduction..."
                rows={12}
                className="font-mono text-sm"
              />
            </CardContent>
          </Card>

          {/* Chapitres */}
          {bookData.chapters.map((chapter, index) => (
            <Card key={index}>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Chapitre {chapter.chapter_number}</CardTitle>
                  </div>
                  <Button
                    type="button"
                    variant="destructive"
                    size="sm"
                    onClick={() => removeChapter(index)}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>Titre du chapitre</Label>
                  <Input
                    value={chapter.chapter_title}
                    onChange={(e) => handleChapterChange(index, "chapter_title", e.target.value)}
                    placeholder={`Chapitre ${chapter.chapter_number}`}
                  />
                </div>
                <div>
                  <Label>Contenu</Label>
                  <Textarea
                    value={chapter.content}
                    onChange={(e) => handleChapterChange(index, "content", e.target.value)}
                    placeholder="Collez ici le texte du chapitre..."
                    rows={12}
                    className="font-mono text-sm"
                  />
                </div>
              </CardContent>
            </Card>
          ))}

          {/* Bouton Ajouter Chapitre */}
          <Button
            type="button"
            variant="outline"
            onClick={addChapter}
            className="w-full"
          >
            <Plus className="w-4 h-4 mr-2" />
            Ajouter un chapitre
          </Button>

          {/* Conclusion */}
          <Card>
            <CardHeader>
              <CardTitle>Conclusion</CardTitle>
            </CardHeader>
            <CardContent>
              <Textarea
                value={bookData.conclusion}
                onChange={(e) => setBookData({ ...bookData, conclusion: e.target.value })}
                placeholder="Collez ici le texte de la conclusion..."
                rows={12}
                className="font-mono text-sm"
              />
            </CardContent>
          </Card>

          {/* Bouton Sauvegarder */}
          <div className="flex justify-end">
            <Button
              type="submit"
              disabled={loading}
              className="bg-green-600 hover:bg-green-700 text-white px-8 py-3"
              size="lg"
            >
              <Save className="mr-2 h-5 w-5" />
              {loading ? "Sauvegarde..." : "Enregistrer le Livre"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AdminBookContentPage;
